import { isValidDate, parseToDate } from '@global/utils/date';
import { Gender } from '@global/utils/domain/entities';
import { AddQuizB2BMutation, B2b_Diagnosis_V1_Insert_Input, Mutation_Root } from '@global/utils/remote-graphql-types';
import defaultClient from '@lp-root/src/data/graphql/default-client';
import { InsertB2bDiagnosisMutation } from '@lp-root/src/data/graphql/hasura/b2b-diagnosis-mutation.mutation.hasura';
import QuizTypeformDataSource from '@lp-root/src/data/quiz-typeform.datasource';
import {
  Anxiety,
  Concerned,
  ControlConcerns,
  Discouragement,
  InitialInsomnia,
  Interference,
  MiddleInsomnia,
  OthersPerception,
  Pleasure,
  QuizB2BData,
  Satisfaction,
  SleepDisturb,
  SleepProblemAge,
  TerminalInsomnia,
  WeekFrequency,
} from '@lp-root/src/data/quiz-typeform.model';
import {
  mapConcernedToNumber,
  mapInitialInsomniaToNumber,
  mapInterferenceToNumber,
  mapMiddleInsomniaToNumber,
  mapOthersPerceptionToNumber,
  mapSatisfactionToNumber,
  mapTerminalInsomniaToNumber,
} from '@lp-root/src/modules/quiz-b2b/quiz-b2b-questions';
import { FormData } from '@web/atomic/legacy/obj.form';
import { isCpfValid } from '@global/utils/domain/cpf';
import { useQueryParams } from '@web/atomic/obj.custom-hooks/query-params';
import { ApolloError, MutationTuple } from '@web/data/apollo-client';
import { useMutationCustom } from '@web/data/use-mutation-custom.hook';
import { navigate } from 'gatsby';
import QueryString from 'querystringify';
import React, { useCallback, useEffect, useState } from 'react';
import {
  AnxietyQuestion,
  BirthdateQuestion,
  CompanyQuestion,
  ConcernedQuestion,
  ConsentAnswer,
  ConsentTerm,
  ControlConcernsQuestion,
  CPFQuestion,
  DiscouragementQuestion,
  GenderQuestion,
  InitialInsomniaQuestion,
  InterferenceQuestion,
  Introduction,
  MiddleInsomniaQuestion,
  OthersPerceptionQuestion,
  PleasureQuestion,
  QuestionName,
  QuestionProps,
  SatisfactionQuestion,
  SleepDisturbQuestion,
  SleepProblemAgeQuestion,
  TerminalInsomniaQuestion,
  WeekFrequencyQuestion,
} from './quiz-typeform-questions';
import { buildQuizB2BMutationVar } from './quiz-typeform.utils';

interface FormControllerNextPageQueryParams {
  age: string;
  score: string;
  cause: string;
}

interface FormControllerResponse<QuestionProps, QuizB2BData> {
  initialized: boolean;
  onNext: (component: React.FunctionComponent<QuestionProps>, formData?: FormData<QuizB2BData>) => void;
  onBack: (component: React.FunctionComponent<QuestionProps>, formData?: FormData<QuizB2BData>) => void;
  viewToShow: React.FunctionComponent<QuestionProps>;
  viewToShowIsLast: boolean;
  questions: React.FunctionComponent<QuestionProps>[];
  handleSubmit: (formData: FormData<QuizB2BData>) => Promise<void>;
  submitData: AddQuizB2BMutation;
  error: ApolloError;
  submitLoading: boolean;
  initialValues: Partial<QuizB2BData>;
}

const questions: React.FunctionComponent<QuestionProps>[] = [
  Introduction,
  ConsentTerm,
  QuestionName,
  InitialInsomniaQuestion,
  MiddleInsomniaQuestion,
  TerminalInsomniaQuestion,
  SatisfactionQuestion,
  InterferenceQuestion,
  OthersPerceptionQuestion,
  ConcernedQuestion,
  SleepProblemAgeQuestion,
  WeekFrequencyQuestion,
  SleepDisturbQuestion,
  PleasureQuestion,
  DiscouragementQuestion,
  AnxietyQuestion,
  ControlConcernsQuestion,
  CompanyQuestion,
  GenderQuestion,
  CPFQuestion,
  BirthdateQuestion,
];
const allViews: { [key: string]: React.FunctionComponent<QuestionProps> } = {};
questions.forEach((val, i) => {
  /** SMELL: displayName wont work at build time (the value will be undefined), the next line is used to make sure displayName is set */
  val.displayName = `${i}_${val.name}`;

  allViews[val.displayName] = val;
});

export function useFormController(resultsPath: string): FormControllerResponse<QuestionProps, QuizB2BData> {
  const [initialized, setInitialized] = useState(false);
  const [viewToShowName, setViewToShowName] = useState(Introduction.displayName);
  const [viewToShowIsLast, setViewToShowIsLast] = useState(false);

  const [initialValues, setInitialValues] = useState<Partial<QuizB2BData>>({});

  const navigateToResult = (): void => {
    const data = QuizTypeformDataSource.quizTypeform;
    const nextPageParams: FormControllerNextPageQueryParams = {
      age: data.sleepProblemAge,
      cause: data.sleepDisturb,
      score: Math.floor(computeScore({ data, other: null, error: null })).toString(),
    };
    const url = `${resultsPath}${QueryString.stringify(nextPageParams, true)}`;
    navigate(url);
  };

  const prefilledValues = useQueryParams<Partial<QuizB2BData>>();
  prefilledValues.gender = mapBrazilianGender(prefilledValues.gender);
  useEffect(() => {
    if (prefilledValues.email) {
      QuizTypeformDataSource.quizTypeform = Object.assign({ email: prefilledValues.email }, QuizTypeformDataSource.quizTypeform);
    }
    const value = Object.assign({}, prefilledValues, QuizTypeformDataSource.quizTypeform ?? {});
    setInitialValues(value);

    const userHasStartedQuestionnaire = value.term;
    if (!userHasStartedQuestionnaire) {
      setInitialized(true);
      return;
    }

    const nextQuestion = getSuccedingQuestionComponent(value, Introduction, { data: value as QuizB2BData, other: null, error: null });
    if (nextQuestion) {
      setInitialized(true);
      setViewToShowName(nextQuestion.displayName);
    } else {
      navigateToResult();
    }
  }, [JSON.stringify(prefilledValues)]);

  const [performSubmitRequest, { data: submitData, error, loading: submitLoading }] = useDiagnosisMutation(navigateToResult);

  const handleSubmit = useCallback(
    async (formData: FormData<QuizB2BData>) => {
      if (Object.keys(formData.error).length !== 0) return;
      formData.data.company = formData.data.company ?? initialValues.company;
      formData.data.origin = initialValues?.origin;
      const quizB2B = buildQuizB2BMutationVar(formData.data);
      await performSubmitRequest({ variables: { quizB2B } });
    },
    [performSubmitRequest, initialValues?.company, initialValues?.origin]
  );

  const onNext = (component: React.FunctionComponent<QuestionProps>, formData: FormData<QuizB2BData>): void => {
    QuizTypeformDataSource.quizTypeform = Object.assign(QuizTypeformDataSource.quizTypeform, formData?.data);

    const currentQuestionComponent = allViews[component.displayName];
    if (!currentQuestionComponent) return;

    const nextQuestion = getSuccedingQuestionComponent(prefilledValues, currentQuestionComponent, formData);
    if (!nextQuestion) {
      return;
    }

    setViewToShowName(nextQuestion.displayName);
    const isLastQuestion = !getSuccedingQuestionComponent(prefilledValues, nextQuestion, formData);
    setViewToShowIsLast(isLastQuestion);
  };

  const onBack = (component: React.FunctionComponent<QuestionProps>, formData: FormData<QuizB2BData>): void => {
    const currentQuestionComponent = allViews[component.displayName];
    if (!currentQuestionComponent) return;

    const isFirstQuestionActive = currentQuestionComponent === questions[0];
    if (isFirstQuestionActive) {
      return;
    }

    const nextQuestion = getPrecedingQuestionComponent(prefilledValues, currentQuestionComponent, formData);
    setViewToShowName(nextQuestion.displayName);
    setViewToShowIsLast(false);
  };

  /** disable "enter" key from submiting form: https://stackoverflow.com/a/37242043/3670829 */
  useEffect(() => {
    const onKeyDown = (e): boolean => {
      if (
        (e.keyIdentifier == 'U+000A' || e.keyIdentifier == 'Enter' || e.keyCode == 13) &&
        e.target.nodeName == 'INPUT' &&
        e.target.type == 'text'
      ) {
        e.preventDefault();
        return false;
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return (): void => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  return {
    initialized,
    onNext,
    onBack,
    handleSubmit,
    viewToShow: allViews[viewToShowName],
    viewToShowIsLast,
    questions: questions,
    submitData,
    error,
    submitLoading,
    initialValues,
  };
}

////////////////////////////////////////////////////////////////////////////////////////////////////

/** this method may be necessary if the gender is set in portuguese */
export const mapBrazilianGender = (gender: string): Gender => {
  if (!gender || gender === 'undefined') {
    return undefined;
  }

  const normalizedGender = gender.toLowerCase();
  switch (normalizedGender) {
    case 'masculino':
      return Gender.Male;
    case 'feminino':
      return Gender.Female;
    case 'outros':
      return Gender.Other;
    case 'outro':
      return Gender.Other;
    default:
      return normalizedGender as Gender;
  }
};

function useDiagnosisMutation(onCompleted: () => void): MutationTuple<AddQuizB2BMutation, { quizB2B: B2b_Diagnosis_V1_Insert_Input }> {
  return useMutationCustom(InsertB2bDiagnosisMutation, {
    onCompleted,
    client: defaultClient,
  });
}

const getSuccedingQuestionComponent = (
  prefilledValues: Partial<QuizB2BData> = {},
  questionComponent: React.FunctionComponent<QuestionProps>,
  formData: FormData<QuizB2BData>
): React.FunctionComponent<QuestionProps> => {
  return getNextQuestion('succeding', prefilledValues, questionComponent, formData);
};

const getPrecedingQuestionComponent = (
  prefilledValues: Partial<QuizB2BData> = {},
  questionComponent: React.FunctionComponent<QuestionProps>,
  formData: FormData<QuizB2BData>
): React.FunctionComponent<QuestionProps> => {
  return getNextQuestion('preceding', prefilledValues, questionComponent, formData);
};

const getNextQuestion = (
  direction: 'preceding' | 'succeding',
  prefilledValues: Partial<QuizB2BData> = {},
  questionComponent: React.FunctionComponent<QuestionProps>,
  formData: FormData<QuizB2BData>
): React.FunctionComponent<QuestionProps> => {
  const currentQuestionIndex = questions.indexOf(questionComponent);
  const previousProbableQuestionIndex = currentQuestionIndex + (direction === 'preceding' ? -1 : +1);
  const previousProbableComponent = questions[previousProbableQuestionIndex];
  const skip = canSkipQuestion(prefilledValues, previousProbableComponent, formData);

  if (skip) {
    return getNextQuestion(direction, prefilledValues, previousProbableComponent, formData);
  } else {
    return previousProbableComponent;
  }
};

const canSkipQuestion = (
  filledValues: Partial<QuizB2BData> = {},
  questionComponent: React.FunctionComponent<QuestionProps>,
  formData: FormData<QuizB2BData>
): boolean => {
  switch (questionComponent) {
    case Introduction:
      return !!filledValues.term;
    case ConsentTerm:
      return filledValues.term === ConsentAnswer.Accepted;
    case QuestionName:
      return !!filledValues.name;
    case InitialInsomniaQuestion:
      return Object.values(InitialInsomnia).includes(filledValues.initialInsomnia);
    case MiddleInsomniaQuestion:
      return Object.values(MiddleInsomnia).includes(filledValues.middleInsomnia);
    case TerminalInsomniaQuestion:
      return Object.values(TerminalInsomnia).includes(filledValues.terminalInsomnia);
    case SatisfactionQuestion:
      return Object.values(Satisfaction).includes(filledValues.satisfaction);
    case InterferenceQuestion:
      return Object.values(Interference).includes(filledValues.interference);
    case OthersPerceptionQuestion:
      return Object.values(OthersPerception).includes(filledValues.othersPerception);
    case ConcernedQuestion:
      return Object.values(Concerned).includes(filledValues.concerned);
    case SleepProblemAgeQuestion:
      return Object.values(SleepProblemAge).includes(filledValues.sleepProblemAge) || !checkSleepProblemAge(formData);
    case WeekFrequencyQuestion:
      return Object.values(WeekFrequency).includes(filledValues.weekFrequency) || !checkSleepProblemAge(formData);
    case SleepDisturbQuestion:
      return Object.values(SleepDisturb).includes(filledValues.sleepDisturb);
    case PleasureQuestion:
      return Object.values(Pleasure).includes(filledValues.pleasure);
    case DiscouragementQuestion:
      return Object.values(Discouragement).includes(filledValues.discouragement);
    case AnxietyQuestion:
      return Object.values(Anxiety).includes(filledValues.anxiety);
    case ControlConcernsQuestion:
      return Object.values(ControlConcerns).includes(filledValues.controlConcerns);
    case CompanyQuestion:
      return !!filledValues.company;
    case GenderQuestion:
      return Object.values(Gender).includes(mapBrazilianGender(filledValues.gender));
    case CPFQuestion:
      return filledValues.cpf && isCpfValid(filledValues.cpf);
    case BirthdateQuestion:
      return filledValues.birthdate && isValidDate(parseToDate(filledValues.birthdate));
    default:
      return false;
  }
};

const checkSleepProblemAge = (formData: FormData<QuizB2BData>): boolean => {
  const minPoinsNecessaryToShowSleepProblemAgeQuestion = 7;
  const showSleepProblemAgeQuestion = isiSum(formData);
  return showSleepProblemAgeQuestion >= minPoinsNecessaryToShowSleepProblemAgeQuestion;
};

////////////////////////////////////////////////////////////////////////////////////////////////////

const computeScore = (formData: FormData<Partial<QuizB2BData>>): number => {
  let score = isiSum(formData);

  const factorQuestionThree = 2.8;
  const factorQuestionFourOne = 10;
  const factorQuestionFourTwo = -1;
  const factorQuestionFourThree = 10;
  // Q3
  score /= factorQuestionThree;

  // Q4_1
  score -= factorQuestionFourOne;

  //Q4_2
  score *= factorQuestionFourTwo;

  //Q4_3
  score *= factorQuestionFourThree;

  return score;
};

const isiSum = (formData: FormData<Partial<QuizB2BData>>): number => {
  const sum =
    mapInitialInsomniaToNumber(formData.data.initialInsomnia) +
    mapMiddleInsomniaToNumber(formData.data.middleInsomnia) +
    mapTerminalInsomniaToNumber(formData.data.terminalInsomnia) +
    mapSatisfactionToNumber(formData.data.satisfaction) +
    mapInterferenceToNumber(formData.data.interference) +
    mapOthersPerceptionToNumber(formData.data.othersPerception) +
    mapConcernedToNumber(formData.data.concerned);

  return sum;
};
